import { Card, Flex, Layout, Spin, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../components/Button"
import RestorePasswordForm from "../components/Forms/RestorePasswordForm"
import LoginHeader from "../components/LoginHeader"
import PrivacyPolicy from "../components/PrivacyPolicy"
import { generateDocumentTitle } from "../constants/document-title"
import { type RootState } from "../store"
import { authErrorReset } from "./LoginPage/redux/auth.slice"
import { checkPassword } from "./LoginPage/redux/auth.thunks"

const { Text, Title, Paragraph } = Typography

function RestorePasswordPage({
  error,
  fetching,
  checkPassword,
  resetError
}: ConnectedProps<typeof connector>) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [firstLoading, setFirstLoading] = useState(true)

  function handleSubmit(data: { email: string }) {
    checkPassword({ email: data.email, navigate })
  }

  useEffect(() => {
    generateDocumentTitle("Forgot Password")
    if (firstLoading) {
      resetError()
      setFirstLoading(false)
    }
  }, [])

  useEffect(() => {
    if (error) resetError()
  }, [error])

  return (
    <Layout style={{ backgroundColor: "#e6f2f5", minHeight: "100vh" }}>
      <Spin size="large" spinning={fetching}>
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ minHeight: "100vh" }}
        >
          <LoginHeader />

          <Card
            className="login-card"
            style={{ margin: "auto", width: "100%", maxWidth: "540px" }}
          >
            <Flex vertical align="center" style={{ marginBottom: 16 }}>
              <Title level={2} style={{ marginBottom: 6 }}>
                {t("Reset Password")}
              </Title>
              <Paragraph type="secondary" style={{ fontSize: 16, margin: 0 }}>
                {t(
                  "Include the email address associated with your account and we’ll send you an email with instructions to reset your password."
                )}
              </Paragraph>
            </Flex>

            <RestorePasswordForm
              onSubmit={handleSubmit}
              error={!firstLoading && error?.message}
            >
              <ButtonComponent
                size="large"
                type="text"
                onClick={() => navigate("/login")}
                style={{ marginTop: "16px" }}
                block
              >
                <Text type="secondary">{t("Remember your password?")} </Text>
                <Text className="text-link">{t("Back to sign in")}</Text>
              </ButtonComponent>
            </RestorePasswordForm>
          </Card>

          <PrivacyPolicy />
        </Flex>
      </Spin>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    error: state.auth.error,
    fetching: state.auth.fetching
  }
}

// wraps dispatch to create nicer functions to call within our component
const mapDispatchToProps = {
  checkPassword: checkPassword,
  resetError: authErrorReset
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(RestorePasswordPage)
